import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  uiStringDetailSchema,
  uiStringSchema,
  type ListUiStringsParams,
  type UiString,
  type UiStringDetail,
  type UiStringFormValues,
} from './schema';

const BASE_PATH = '/ui-strings';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(uiStringSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: uiStringDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

// Drop blank rows; trim values.
function toWireTranslations(values: UiStringFormValues) {
  return values.translations
    .filter((t) => t.value.trim().length > 0)
    .map((t) => ({ localeCode: t.localeCode, value: t.value.trim() }));
}

// `key` is intentionally absent — immutable post-create (server forbids non-whitelisted).
// Empty description string maps to `null` to clear the field cleanly.
function toUpdatePayload(values: UiStringFormValues) {
  const trimmedDesc = values.description.trim();
  return {
    value: values.value,
    description: trimmedDesc.length === 0 ? null : trimmedDesc,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

export async function listUiStrings(
  params: ListUiStringsParams,
): Promise<CommonResponse<UiString>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getUiString(id: string): Promise<UiStringDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateUiString(
  id: string,
  values: UiStringFormValues,
): Promise<UiStringDetail> {
  const response = await apiClient.put<ApiResponseShape<UiStringDetail>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}
